import { IApiUnit, IRawIncomeDataInfoFieldValue } from "@propely/propely-typings";

const ANNUAL_PERIOD = 12;
type RentType = "areaRent" | "commonRent" | "parkingRent" | "otherRent";

export const standardizeDateToStart = (date: Date) => {
  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date;
};

export const standardizeDateToEnd = (date: Date) => {
  date.setHours(23);
  date.setMinutes(59);
  date.setSeconds(59);
  date.setMilliseconds(999);
  return date;
};

export const getIncomeDataInfoFieldValues = (units: IApiUnit[]) => {
  const incomeDataInfoFieldValues: IRawIncomeDataInfoFieldValue[] = [];
  for (const unit of units) {
    const incomeDataInfoField = unit.fields?.incomeData;
    if (incomeDataInfoField) {
      incomeDataInfoFieldValues.push(incomeDataInfoField);
    }
  }
  return incomeDataInfoFieldValues;
};

export const calculateTotalAnnualRent = (incomeRents: IRawIncomeDataInfoFieldValue[]) => {
  const rentTypes: RentType[] = ["areaRent", "parkingRent", "otherRent"];
  let totalRent = 0;
  for (const incomeRent of incomeRents) {
    for (const rentType of rentTypes) {
      const rents = incomeRent?.value?.[rentType];
      if (rents) {
        for (const rent of rents) {
          totalRent += (((rent.quantity ?? 1) * rent.sum) / (rent.periodType ?? 1)) * ANNUAL_PERIOD;
        }
      }
    }
  }
  return totalRent;
};

export const calculateTotalAnnualSpecificRent = (incomeRents: IRawIncomeDataInfoFieldValue[], rentType: RentType) => {
  let totalRent = 0;
  for (const incomeRent of incomeRents) {
    const rents = incomeRent?.value?.[rentType];
    if (rents) {
      for (const rent of rents) {
        totalRent += (((rent.quantity ?? 1) * rent.sum) / (rent.periodType ?? 1)) * ANNUAL_PERIOD;
      }
    }
  }
  return totalRent;
};

export const calculateDaysLeftOfContract = (contractEndDate: string | Date | undefined | null): number | undefined => {
  if (contractEndDate && contractEndDate) {
    const endDate = standardizeDateToStart(new Date(contractEndDate));
    const today = standardizeDateToStart(new Date());
    const msToEndDate = endDate.getTime() - today.getTime();
    const daysToEndDate = msToEndDate / (1000 * 60 * 60 * 24);
    return daysToEndDate;
  }
  return undefined;
};

export const calculateTotalContractDays = (
  contractStartDate: string | Date | undefined,
  contractEndDate: string | Date | undefined
): number | undefined => {
  if (contractStartDate && contractEndDate) {
    const startDate = standardizeDateToStart(new Date(contractStartDate));
    const endDate = standardizeDateToStart(new Date(contractEndDate));
    const msToEndDate = endDate.getTime() - startDate.getTime();
    const daysToEndDate = msToEndDate / (1000 * 60 * 60 * 24);
    return daysToEndDate;
  }
  return undefined;
};

export const calculateTotalContractValue = (
  incomeRent: IRawIncomeDataInfoFieldValue | undefined,
  contractStartDate: string | Date | undefined,
  contractEndDate: string | Date | undefined
) => {
  if (!incomeRent) return undefined;

  const totalContractDays = calculateTotalContractDays(contractStartDate, contractEndDate);
  if (totalContractDays) {
    const totalContractValue = calculateTotalAnnualRent([incomeRent]) * (totalContractDays / 365);
    return totalContractValue;
  }
  return undefined;
};

export const calculateRemainingContractValue = (
  incomeRent: IRawIncomeDataInfoFieldValue | undefined,
  contractEndDate: string | Date | undefined | null
) => {
  if (!incomeRent) return 0;

  const daysLeftOfContract = calculateDaysLeftOfContract(contractEndDate);
  if (daysLeftOfContract) {
    const totalContractValue = calculateTotalAnnualRent([incomeRent]) * (daysLeftOfContract / 365);
    return totalContractValue;
  }
  return 0;
};

export const isUnitContractActive = (
  contractStartDate: string | Date | undefined | null,
  contractEndDate: string | Date | undefined | null
): boolean => {
  if (contractStartDate && standardizeDateToStart(new Date(contractStartDate)) >= new Date()) return false;
  if (contractEndDate && standardizeDateToEnd(new Date(contractEndDate)) < new Date()) return false;
  return true;
};

export const isUnitContractExpired = (contractEndDate: string | Date | undefined | null): boolean => {
  if (contractEndDate && standardizeDateToEnd(new Date(contractEndDate)) < new Date()) return true;
  return false;
};

export const isUnitContractFuture = (contractStartDate: string | Date | undefined | null): boolean => {
  if (contractStartDate && standardizeDateToStart(new Date(contractStartDate)) >= new Date()) return true;
  return false;
};

